import React, { useMemo } from 'react';
import { StateCreator } from 'zustand';
import { definePersistentQueryParamStore, PersistQueryParamsConfig } from '@warehouse/shared/util';
import { createPaginatorSlice, createSearchSlice } from '@warehouse/shared/ui';
import { relationshipRepositorySingleton } from '@warehouse/relationships/infra';

import { Subscription } from 'rxjs';
import { SearchRelationshipsOutput } from '@warehouse/relationships/core';
import { RelationshipFilters } from '@warehouse/relationships/domain';
import isEqual from 'lodash/isEqual';
import { createSearchFieldsSlice } from '../../../shared/ui/search-fields/search-fields.store';
import { ChildrenListStore } from './children.type';
import { createChildrenTableManagerSliceFactory } from './children-table-manager.store';
import { paginatorStoreConfig, tableManagerStoreConfig } from './children-table.store-config';
import { createFilteringDataSlice } from './parentTitle.store';
import { ChildrenColumnName, childrenSortToApiSort } from '../children-sort';
import { ChildrenTableType } from '../children-table-type';

export const createChildrenListStore: StateCreator<ChildrenListStore> = (set, get, store) => {
	const repo = relationshipRepositorySingleton.get();
	let watchDrillDownSearchSubscription: Subscription | undefined;
	const unsubscribeFromDrillDownSearch = () => {
		if (watchDrillDownSearchSubscription) watchDrillDownSearchSubscription.unsubscribe();
	};

	const subscribeToDrillDownSearch = () => {
		unsubscribeFromDrillDownSearch();
		const state = get();
		set({
			isPending: true,
		});
		watchDrillDownSearchSubscription = repo
			.watchSearch({
				searchValue: state.searchValue,
				searchFields: state.searchFields,
				page: state.page,
				perPage: state.perPage,
				filtering: RelationshipFilters.childrenOf({
					titleUuid: state.parentTitle.titleUuid,
					excludeHidden: !state.parentTitle.showHiddenTitles,
					relationshipType: state.parentTitle.relationshipType,
				}),
				sorting: childrenSortToApiSort(state.sort, state.parentTitle.titleUuid),
				pollInterval: 500,
			})
			.subscribe((data: SearchRelationshipsOutput) => {
				set({
					items: data.documents,
					page: data.page.currentPage,
					totalPages: data.page.totalPage,
					totalCount: data.page.totalDocument,
					perPage: data.page.perPage,
					isPending: false,
				});
			});
	};

	// Subscribe to the store's state changes to update the items subscription when necessary.
	const childrenTableManagerStore = createChildrenTableManagerSliceFactory({ getPaginatorStore: get })(set, get, store);
	const paginatorStore = createPaginatorSlice(set, get, store);
	const searchStore = createSearchSlice(set, get, store);
	const searchFieldsStore = createSearchFieldsSlice(set, get, store);
	const parentTitleStore = createFilteringDataSlice(set, get, store);

	store.subscribe((state, prevState) => {
		if (
			!isPaginationEqual(state, prevState) ||
			!isParentTitleEqual(state.parentTitle, prevState?.parentTitle) ||
			!isSearchValueEqual(state.searchValue, prevState?.searchValue) ||
			!isSearchFieldsEqual(state.searchFields, prevState?.searchFields) ||
			!isEqual(state.sort, prevState?.sort)
		) {
			subscribeToDrillDownSearch();
		}
	});
	return {
		...childrenTableManagerStore,
		...paginatorStore,
		...searchStore,
		...searchFieldsStore,
		...parentTitleStore,
		items: [],
		isPending: false,
		actions: {
			...childrenTableManagerStore.actions,
			...paginatorStore.actions,
			...searchStore.actions,
			...searchFieldsStore.actions,
			...parentTitleStore.actions,
		},
	} satisfies ChildrenListStore;
};

const [ChildrenStoreProviderInternal, useChildrenListStore] =
	definePersistentQueryParamStore<ChildrenListStore>(createChildrenListStore);

export { useChildrenListStore };

export function ChildrenListStoreProvider({
	children,
	tableType,
	sortableColumns,
	configOverride,
}: {
	tableType: ChildrenTableType;
	sortableColumns: readonly ChildrenColumnName[];
	configOverride?: PersistQueryParamsConfig<ChildrenListStore>;
	children: React.ReactNode;
}) {
	const config = useMemo(
		() => ({
			...tableManagerStoreConfig(tableType, sortableColumns),
			...paginatorStoreConfig,
			...(configOverride || {}),
		}),
		[tableType, sortableColumns, configOverride],
	);

	return <ChildrenStoreProviderInternal config={config}>{children}</ChildrenStoreProviderInternal>;
}

function isPaginationEqual(state: ChildrenListStore, prevState?: ChildrenListStore) {
	return state.page === prevState?.page && state.perPage === prevState?.perPage;
}
function isParentTitleEqual(
	currentParentTitle: ChildrenListStore['parentTitle'],
	prevParentTitle?: ChildrenListStore['parentTitle'],
) {
	if (!prevParentTitle) return false;
	return (
		currentParentTitle.titleUuid === prevParentTitle.titleUuid &&
		currentParentTitle.showHiddenTitles === prevParentTitle.showHiddenTitles &&
		currentParentTitle.relationshipType === prevParentTitle.relationshipType &&
		JSON.stringify(currentParentTitle.includedWorkTypes) === JSON.stringify(prevParentTitle.includedWorkTypes)
	);
}

function isSearchValueEqual(current: string, prev?: string) {
	return current === prev;
}

function isSearchFieldsEqual(current: string[], prev?: string[]) {
	if (!prev) return false;
	if (current.length !== prev.length) return false;
	return current.every((field, index) => field === prev[index]);
}
